
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useMedia } from "./context";
import { MediaItemData } from "./types/mediaTypes";
import { Button } from "@/components/ui/button";
import { Edit, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

interface MediaItemGridProps {
  onEditItem: (item: MediaItemData) => void;
}

const MediaItemGrid: React.FC<MediaItemGridProps> = ({ onEditItem }) => {
  const { language } = useLanguage();
  const { mediaItems, isEditMode, addNewMediaItem, deleteMediaItem } = useMedia();
  const { toast } = useToast();

  if (!isEditMode) {
    return null;
  }

  const handleAddItem = (type: "infographic" | "photo" | "video") => {
    const newItem = addNewMediaItem(type);
    onEditItem(newItem);
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteMediaItem(id);
      toast({
        title: language === "en" ? "Media deleted" : "تم حذف الوسائط",
        description: language === "en" ? "Media item has been deleted" : "تم حذف عنصر الوسائط"
      });
    } catch (error) {
      toast({
        title: language === "en" ? "Delete failed" : "فشل الحذف",
        description: language === "en" ? "An error occurred while deleting the media item" : "حدث خطأ أثناء حذف عنصر الوسائط",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto px-4 mt-4 mb-6">
      {/* Add new item buttons */}
      <div className="flex space-x-2 mb-6">
        <Button 
          className="bg-palsustain-green hover:bg-palsustain-green/90"
          onClick={() => handleAddItem("infographic")}
        >
          <Plus className="mr-2 h-4 w-4" />
          {language === "en" ? "Add Infographic" : "إضافة رسم بياني"}
        </Button>
        <Button 
          className="bg-palsustain-green hover:bg-palsustain-green/90"
          onClick={() => handleAddItem("photo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          {language === "en" ? "Add Photo" : "إضافة صورة"}
        </Button>
        <Button 
          className="bg-palsustain-green hover:bg-palsustain-green/90"
          onClick={() => handleAddItem("video")}
        >
          <Plus className="mr-2 h-4 w-4" />
          {language === "en" ? "Add Video" : "إضافة فيديو"}
        </Button>
      </div>

      {/* Media items grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {mediaItems.map((item) => (
          <div key={`edit-control-${item.id}`} className="relative">
            <div className="absolute top-2 left-2 z-20 flex space-x-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => onEditItem(item)}
                className="rounded-full bg-white/80 backdrop-blur-sm p-2 h-8 w-8 hover:bg-white"
              >
                <Edit className="h-4 w-4" />
              </Button>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="rounded-full bg-white/80 backdrop-blur-sm p-2 h-8 w-8 hover:bg-white hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {language === "en" ? "Are you sure?" : "هل أنت متأكد؟"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {language === "en" 
                        ? "This action cannot be undone. This will permanently delete the media item."
                        : "لا يمكن التراجع عن هذا الإجراء. سيؤدي هذا إلى حذف عنصر الوسائط نهائيًا."
                      }
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>
                      {language === "en" ? "Cancel" : "إلغاء"}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => handleDeleteItem(item.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      {language === "en" ? "Delete" : "حذف"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
            
            <div className="bg-gray-100 aspect-video rounded-lg overflow-hidden relative">
              {item.image ? (
                <img 
                  src={item.image} 
                  alt={item.title[language] || "Media Item"} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/placeholder.svg";
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <span className="text-gray-400">{language === "en" ? "No Image" : "لا توجد صورة"}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                <h3 className="text-white font-medium truncate">
                  {item.title[language] || (language === "en" ? "Untitled" : "بدون عنوان")}
                </h3>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaItemGrid;
